import { useState } from 'react';
import {
  Search, Filter, Calendar, DollarSign, Building2,
  ExternalLink, RefreshCcw, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, AlertTriangle, TrendingUp, Bookmark, Trophy, PlusCircle, X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import {
  useAtualizarLicitacoes, useLicitacoesFeed, useLicitacoesFontes, useGastoPorNicho, useLicitacaoVencedores,
  type LicitacaoFeed, type LicitacoesFilters, type GastoPorNichoFilters
} from '@/hooks/useLicitacoes';
import { useLicitacoesMeta } from '@/hooks/useLicitacoesMeta';
import { BuscasSalvasModal } from '@/components/licitacoes/BuscasSalvasModal';
import { LicitacaoFeedDetalhesModal } from '@/components/licitacoes/LicitacaoFeedDetalhesModal';
import { AdicionarLicitacaoPipelineModal } from '@/components/licitacoes/AdicionarLicitacaoPipelineModal';
import { FiltrosAvancadosLicitacoes } from '@/components/licitacoes/FiltrosAvancadosLicitacoes';
import {
  filtrosPersistiveis, listarFiltrosAtivos, normalizarFiltrosLicitacoes,
} from '@/components/licitacoes/filtrosAtivos';
import type { BuscaSalvaFiltros } from '@/hooks/useBuscasSalvas';
import { useEmpresas } from '@/hooks/useEmpresa';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('pt-BR');

function GastoPorNichoCard({ filters }: { filters: GastoPorNichoFilters }) {
  const temNicho = !!(
    filters.search || filters.categoria ||
    (filters.objeto_inclui && filters.objeto_inclui.length > 0)
  );
  const { data, isLoading } = useGastoPorNicho(filters);

  if (!temNicho) return null;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-ds-success" />
          <h2 className="text-lg font-semibold text-ds-text-primary">Gasto público no nicho filtrado</h2>
        </div>

        {isLoading && (
          <p className="text-sm text-ds-text-secondary">Calculando...</p>
        )}

        {!isLoading && data && (
          <div className="space-y-4">
            <p className="text-2xl font-bold text-ds-success">
              {formatCurrency(data.total_valor_estimado)}
              <span className="text-sm font-normal text-ds-text-secondary ml-2">
                em {data.total_licitacoes.toLocaleString('pt-BR')} licitações
              </span>
            </p>

            {data.por_uf.length > 0 && (
              <div>
                <p className="text-xs font-medium text-ds-text-secondary mb-1.5">Por estado</p>
                <div className="flex flex-wrap gap-2">
                  {data.por_uf.slice(0, 10).map((item) => (
                    <span
                      key={item.uf}
                      className="px-2.5 py-1 rounded-full text-xs bg-ds-surface2 text-ds-text-primary border border-ds-border"
                    >
                      {item.uf}: {formatCurrency(item.total_valor_estimado)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.por_mes.length > 0 && (
              <div>
                <p className="text-xs font-medium text-ds-text-secondary mb-1.5">Por mês</p>
                <div className="flex flex-wrap gap-2">
                  {data.por_mes.map((item) => (
                    <span
                      key={item.mes}
                      className="px-2.5 py-1 rounded-full text-xs bg-ds-surface2 text-ds-text-primary border border-ds-border"
                    >
                      {item.mes}: {formatCurrency(item.total_valor_estimado)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.top_orgaos.length > 0 && (
              <div>
                <p className="text-xs font-medium text-ds-text-secondary mb-1.5">Órgãos que mais compraram</p>
                <ul className="space-y-1">
                  {data.top_orgaos.slice(0, 5).map((item) => (
                    <li key={item.orgao} className="flex justify-between gap-4 text-sm text-ds-text-secondary">
                      <span className="truncate">{item.orgao}</span>
                      <span className="text-ds-text-primary font-medium shrink-0">{formatCurrency(item.total_valor_estimado)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const DEFAULT_FILTERS: LicitacoesFilters = {
  somente_ativas: true,
  sort: 'scraped_at',
  order: 'desc',
  page: 1,
  limit: 20,
};

function VencedorSection({ licitacaoId }: { licitacaoId: number }) {
  const [expanded, setExpanded] = useState(false);
  const { data: vencedores, isLoading } = useLicitacaoVencedores(licitacaoId, expanded);

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((p) => !p);
        }}
        className="flex items-center gap-1 text-xs text-ds-text-secondary hover:text-ds-text-primary"
      >
        <Trophy className="h-3.5 w-3.5" />
        {expanded ? 'Ocultar vencedor' : 'Ver vencedor'}
      </button>

      {expanded && (
        <div className="mt-1.5">
          {isLoading && (
            <p className="text-xs text-ds-text-secondary">Consultando...</p>
          )}

          {!isLoading && vencedores && vencedores.length === 0 && (
            <p className="text-xs text-ds-text-secondary">
              Vencedor ainda não capturado (disponível apenas para licitações do PNCP com contrato já publicado).
            </p>
          )}

          {!isLoading && vencedores && vencedores.length > 0 && (
            <ul className="space-y-1">
              {vencedores.map((vencedor) => (
                <li key={vencedor.id} className="text-xs text-ds-text-secondary">
                  <span className="font-medium text-ds-text-primary">{vencedor.nome_fornecedor}</span>
                  {' '}— CNPJ {vencedor.cnpj_fornecedor}
                  {vencedor.valor_global !== null ? ` — ${formatCurrency(Number(vencedor.valor_global))}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Licitacoes() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [buscasSalvasOpen, setBuscasSalvasOpen] = useState(false);
  const [filters, setFilters] = useState<LicitacoesFilters>(DEFAULT_FILTERS);
  const [detalhesLicitacao, setDetalhesLicitacao] = useState<LicitacaoFeed | null>(null);
  const [pipelineLicitacao, setPipelineLicitacao] = useState<LicitacaoFeed | null>(null);

  const { data: feedData, isLoading, isError } = useLicitacoesFeed(filters);
  const { data: fontes } = useLicitacoesFontes();
  const { data: meta, isError: metaIsError } = useLicitacoesMeta(filters.somente_ativas !== false);
  const { data: empresas } = useEmpresas();
  const atualizarMutation = useAtualizarLicitacoes();

  const licitacoes = feedData?.licitacoes || [];
  const pagination = feedData?.pagination;

  const patchFilters = (patch: Partial<LicitacoesFilters>) => {
    setFilters(prev => ({ ...prev, ...patch, page: 1 }));
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const empresaNome = (id: number) =>
    empresas?.find((empresa) => empresa.id === id)?.razao_social || `#${id}`;

  const filtrosAtivos = listarFiltrosAtivos(filters, { empresaNome });
  const hasActiveFilters = filtrosAtivos.length > 0;

  // Buscas salvas guardam o conjunto completo de filtros (JSONB livre),
  // menos a paginação — o job de alertas reusa exatamente esse objeto.
  const filtrosParaSalvar: BuscaSalvaFiltros = filtrosPersistiveis(filters);

  const aplicarBuscaSalva = (filtrosSalvos: BuscaSalvaFiltros) => {
    setFilters({
      ...DEFAULT_FILTERS,
      ...normalizarFiltrosLicitacoes(filtrosSalvos, meta?.regioes),
    });
    setBuscasSalvasOpen(false);
  };

  const mutationErrorMessage = (atualizarMutation.error as { message?: string } | null)?.message || null;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Licitações</h1>
          <p className="text-ds-text-secondary mt-1">
            {pagination ? (
              <span>{pagination.total.toLocaleString('pt-BR')} licitações encontradas</span>
            ) : (
              'Acompanhe oportunidades atualizadas automaticamente todos os dias'
            )}
          </p>
        </div>

        <Button type="button" isLoading={atualizarMutation.isPending} onClick={() => atualizarMutation.mutateAsync()}>
          <RefreshCcw className="h-4 w-4 mr-2" />
          Atualizar buscas
        </Button>
      </div>

      {mutationErrorMessage && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-ds-danger">Falha ao atualizar manualmente: {mutationErrorMessage}</p>
          </CardContent>
        </Card>
      )}

      {atualizarMutation.isSuccess && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-ds-success">Busca manual concluída. A lista foi atualizada com os dados mais recentes.</p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {/* Linha principal de busca */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-secondary" />
              <Input
                aria-label="Buscar licitações"
                placeholder="Buscar por objeto, órgão ou número..."
                value={filters.search || ''}
                onChange={(e) => patchFilters({ search: e.target.value || undefined })}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen(p => !p)}
                className="gap-1.5"
              >
                <Filter className="h-4 w-4" />
                Filtros
                {hasActiveFilters && (
                  <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-ds-success px-1.5 text-[11px] font-semibold text-white">
                    {filtrosAtivos.length}
                  </span>
                )}
                {filtersOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </Button>

              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setBuscasSalvasOpen(true)}
                aria-label="Buscas salvas e alertas automáticos"
                title="Buscas salvas ativas geram alerta automático de novas licitações"
                className="gap-1.5"
              >
                <Bookmark className="h-4 w-4" />
                Buscas salvas
              </Button>
            </div>
          </div>

          {/* Chips de filtro ativo + contagem de resultados */}
          {hasActiveFilters && (
            <div role="group" aria-label="Filtros ativos" className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-ds-text-secondary">
                {pagination
                  ? `${pagination.total.toLocaleString('pt-BR')} resultados`
                  : 'Resultados'}
                {' · '}
                {filtrosAtivos.length} {filtrosAtivos.length === 1 ? 'filtro ativo' : 'filtros ativos'}
              </span>

              {filtrosAtivos.map((chip) => (
                <span
                  key={chip.id}
                  className="inline-flex items-center gap-1 rounded-full border border-ds-success/30 bg-ds-success/10 px-2.5 py-1 text-xs text-ds-text-primary"
                >
                  {chip.label}
                  <button
                    type="button"
                    aria-label={`Remover filtro ${chip.label}`}
                    onClick={() => patchFilters(chip.patch)}
                    className="rounded-full text-ds-text-secondary hover:text-ds-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-danger"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}

              <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                Limpar tudo
              </Button>
            </div>
          )}

          {/* Painel de filtros avançados */}
          {filtersOpen && (
            <FiltrosAvancadosLicitacoes
              filters={filters}
              onPatch={patchFilters}
              meta={meta}
              metaIndisponivel={metaIsError}
              fontes={fontes}
              empresas={empresas}
            />
          )}
        </CardContent>
      </Card>

      <GastoPorNichoCard filters={filters} />

      {isError && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-ds-danger">Erro ao carregar licitações. Tente novamente em instantes.</p>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="py-10 text-center text-ds-text-secondary">
            Carregando licitações...
          </CardContent>
        </Card>
      )}

      {/* Licitações list */}
      <div className="space-y-4">
        {!isLoading && !isError && licitacoes.map((licitacao) => {
          const isVencida = licitacao.status_calculado === 'vencida';
          const isIncompleta = licitacao.campos_ausentes?.length > 0;

          return (
            <Card
              key={licitacao.id}
              variant="hover"
              role="button"
              tabIndex={0}
              onClick={() => setDetalhesLicitacao(licitacao)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDetalhesLicitacao(licitacao);
                }
              }}
              className="cursor-pointer"
            >
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-ds-text-primary mb-1">
                          {licitacao.objeto}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-ds-text-secondary">
                          <Building2 className="h-4 w-4" />
                          {licitacao.orgao || 'Órgão não informado'}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        {isVencida ? (
                          <Badge variant="default">Vencida</Badge>
                        ) : (
                          <Badge variant="success">Ativa</Badge>
                        )}
                        {isIncompleta && (
                          <span
                            className="flex items-center gap-1 text-xs text-ds-warning cursor-default"
                            title={`Campos ausentes: ${licitacao.campos_ausentes.join(', ')}`}
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Incompleta
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm mb-3">
                      <span className="text-ds-text-secondary">
                        <span className="font-medium text-ds-text-primary">Nº:</span> {licitacao.numero_edital || 'N/A'}
                      </span>
                      <span className="text-ds-text-secondary">
                        <span className="font-medium text-ds-text-primary">Modalidade:</span>{' '}
                        {licitacao.modalidade || 'N/A'}
                      </span>
                      <span className={`text-sm flex items-center gap-1 ${isVencida ? 'text-ds-danger' : 'text-ds-text-secondary'}`}>
                        <Calendar className="h-3.5 w-3.5" />
                        <span className="font-medium text-ds-text-primary">Prazo:</span>{' '}
                        {licitacao.prazo_abertura ? formatDate(licitacao.prazo_abertura) : 'Indefinido'}
                      </span>
                      <span className="text-ds-text-secondary">
                        <span className="font-medium text-ds-text-primary">UF:</span> {licitacao.uf || 'N/A'}
                        {licitacao.municipio ? ` — ${licitacao.municipio}` : ''}
                      </span>
                    </div>

                    {/* Categorias IA */}
                    {licitacao.categorias_ia && licitacao.categorias_ia.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {licitacao.categorias_ia.slice(0, 3).map((cat) => (
                          <span
                            key={cat}
                            className="px-2.5 py-0.5 rounded-full text-xs bg-ds-surface2 text-ds-text-secondary border border-ds-border"
                          >
                            {cat}
                          </span>
                        ))}
                        {licitacao.relevancia_score !== null && (
                          <span className="px-2.5 py-0.5 rounded-full text-xs bg-ds-surface2 text-ds-success border border-ds-border">
                            IA {licitacao.relevancia_score}%
                          </span>
                        )}
                      </div>
                    )}

                    {/* CAPAG + Risco de Deserto + Participantes — badges rotulados (ENR.4/ENR.7).
                        CAPAG e Risco de Deserto são SEMPRE exibidos: com dado → badge azul (info);
                        sem dado → "não encontrada" em badge neutro (default), mas o campo permanece visível. */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {typeof licitacao.participantes_contagem === 'number' && licitacao.participantes_contagem > 0 && (
                        <span title="Fornecedores distintos já observados na sessão do processo (via Radar/PCP). Cobertura parcial — só para processos acompanhados pelo Radar.">
                          <Badge variant="info">{licitacao.participantes_contagem} na disputa</Badge>
                        </span>
                      )}
                      {licitacao.capag_nota ? (
                        <span title="Capacidade de Pagamento do município (Tesouro Nacional). A+/A/B+/B pagam melhor; C/D, pior.">
                          <Badge variant="info">CAPAG: {licitacao.capag_nota}</Badge>
                        </span>
                      ) : (
                        <span title="Ainda não temos a nota CAPAG (Tesouro Nacional) para o município deste edital.">
                          <Badge variant="default">CAPAG: não encontrada</Badge>
                        </span>
                      )}
                      {licitacao.risco_deserto_faixa || licitacao.deserta_confirmada ? (
                        <span
                          title={
                            licitacao.deserta_confirmada
                              ? 'Sinal PRELIMINAR: marcado quando ainda não há contrato publicado no PNCP. Como o contrato costuma sair semanas após a homologação, um edital recém-encerrado pode aparecer aqui sem ter dado deserta de verdade. Confirme na origem.'
                              : 'Estimativa PRELIMINAR pelo histórico de editais semelhantes (cobertura de dados ainda parcial). Risco alto = tende a menos concorrência. Não é garantia — confirme na origem.'
                          }
                        >
                          <Badge variant="info">
                            Risco de Deserto:{' '}
                            {licitacao.deserta_confirmada
                              ? 'Deserta'
                              : licitacao.risco_deserto_faixa === 'alto'
                                ? 'Alto'
                                : licitacao.risco_deserto_faixa === 'medio'
                                  ? 'Médio'
                                  : 'Baixo'}
                            <span className="opacity-70"> (prévia)</span>
                          </Badge>
                        </span>
                      ) : (
                        <span title="Ainda sem estimativa de risco de deserto para este edital (histórico insuficiente para o grupo semelhante).">
                          <Badge variant="default">Risco de Deserto: não encontrada</Badge>
                        </span>
                      )}
                    </div>

                    {/* Aderência por empresa (multi-empresa, Epic K.2) */}
                    {licitacao.aderencias && licitacao.aderencias.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {licitacao.aderencias.map((aderencia) => (
                          <Badge
                            key={aderencia.empresa_id}
                            size="sm"
                            variant={
                              aderencia.recomendacao === 'participar' ? 'success'
                                : aderencia.recomendacao === 'declinar' ? 'danger'
                                  : 'warning'
                            }
                          >
                            {aderencia.empresa_nome}
                            {aderencia.aderencia_score !== null ? ` — ${aderencia.aderencia_score}%` : ''}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Vencedor capturado (Epic N.2.1) */}
                    {isVencida && <VencedorSection licitacaoId={licitacao.id} />}
                  </div>

                  <div className="flex flex-row lg:flex-col items-center lg:items-end gap-4 lg:gap-2">
                    <div className="text-right">
                      <p className="text-sm text-ds-text-secondary">Valor Estimado</p>
                      <p className="text-xl font-bold text-ds-success flex items-center gap-1">
                        <DollarSign className="h-5 w-5" />
                        {licitacao.valor_estimado
                          ? formatCurrency(Number(licitacao.valor_estimado))
                          : <span className="text-ds-text-secondary text-sm font-normal">Não informado</span>
                        }
                      </p>
                    </div>
                    {licitacao.link ? (
                      <a href={licitacao.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        <Button variant="secondary" size="sm" className="min-h-[44px]">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Abrir Licitação
                        </Button>
                      </a>
                    ) : (
                      <Button variant="secondary" size="sm" disabled className="min-h-[44px]">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Sem Link
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="min-h-[44px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPipelineLicitacao(licitacao);
                      }}
                    >
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Adicionar ao pipeline
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {!isLoading && !isError && licitacoes.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-ds-text-secondary">
              Nenhuma licitação encontrada com os filtros selecionados.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Paginação */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!pagination.hasPrevPage}
            onClick={() => setFilters(p => ({ ...p, page: (p.page || 1) - 1 }))}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>

          <span className="text-sm text-ds-text-secondary">
            Página {pagination.page} de {pagination.totalPages}
            {' '}({pagination.total.toLocaleString('pt-BR')} licitações)
          </span>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!pagination.hasNextPage}
            onClick={() => setFilters(p => ({ ...p, page: (p.page || 1) + 1 }))}
          >
            Próxima
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      <BuscasSalvasModal
        isOpen={buscasSalvasOpen}
        onClose={() => setBuscasSalvasOpen(false)}
        filtrosAtuais={filtrosParaSalvar}
        onAplicar={aplicarBuscaSalva}
      />

      <LicitacaoFeedDetalhesModal
        licitacao={detalhesLicitacao}
        empresas={empresas || []}
        onClose={() => setDetalhesLicitacao(null)}
        termoDestaque={filters.search}
      />

      <AdicionarLicitacaoPipelineModal
        licitacao={pipelineLicitacao}
        empresas={empresas || []}
        onClose={() => setPipelineLicitacao(null)}
      />
    </div>
  );
}

export { Licitacoes };
